<script setup lang="ts">
import type { Attr } from "~/types";
import { VueFinalModal } from "vue-final-modal";
import { getCategory } from "~/utils/sekou/category";

const props = defineProps<{
  attrs: Attr[];
  onClose: () => void;
}>();

const route = useRoute();

// queryから選択された分類の形式に変換
function routeToSelectAttr() {
  const attr = route.query.attr as string;
  if (!attr) return [];
  return attr.split(",").map((o) => parseInt(o));
}

// マスター取得
const attrs = computed(() => props.attrs);
const cate = getCategory();

// 選択された分類
const selectAttr = ref<number[]>(routeToSelectAttr());
// 現在選択されている分類の件数
const showCount = ref<number>(0);

// URLのクエリパラメータを監視し、選択された分類を更新する
watch(
  () => route.query.attr,
  () => {
    selectAttr.value = routeToSelectAttr();
  },
);

// 選択された分類の件数を取得
const { data: patternAttrs } = await useFetch(() => {
  return `/api/sekou/search/attr_pattern?pattern=${Array.from(
    selectAttr.value,
  )}`;
});

// 件数を更新する
watch(
  () => patternAttrs.value,
  () => {
    // 選択された分類の件数を取得
    if (selectAttr.value.length && patternAttrs.value?.length) {
      const attr = selectAttr.value[0];
      showCount.value =
        patternAttrs.value?.find((o) => o.attr_num === attr)?.count || 0;
    } else {
      showCount.value = 0;
    }
  },
);

// 選択された分類のみを表示する
const realAttrs = computed<(Attr & { disabled: boolean })[]>(() => {
  // 選択がなければすべてそのまま表示
  if (!patternAttrs?.value?.length)
    return attrs?.value.map((o) => ({ ...o, disabled: false })) ?? [];

  // 選択があった場合、その分類のみ表示する
  const filter_attrs = attrs?.value.map((a) => {
    if (!patternAttrs.value) return { ...a, disabled: false };

    const disabled = !patternAttrs.value.some(
      (attr) => attr.attr_num === a.attr_num,
    );
    return { ...a, disabled };
  });

  if (!filter_attrs) return [];

  return filter_attrs.sort((a, b) => {
    if (a.disabled && !b.disabled) return 1;
    if (!a.disabled && b.disabled) return -1;
    return 0;
  });
});

async function onClick(attr: Attr) {
  if (!selectAttr.value.includes(attr.attr_num)) {
    selectAttr.value.push(attr.attr_num);
  } else {
    selectAttr.value = selectAttr.value.filter((o) => o != attr.attr_num);
  }
}

// 選択をリセット
function reset() {
  selectAttr.value = [];
}

// 検索画面に
async function search() {
  const attr = selectAttr.value;
  attr.sort();
  await navigateTo({
    path: "/sekou/search.php",
    query: {
      attr: attr.join(","),
    },
  });
  await nextTick();
  props.onClose();
}
</script>

<template>
  <VueFinalModal
    content-class="absolute inset-0"
    content-transition="vfm-slide-down"
    swipe-to-close="down"
    :threshold="200"
  >
    <div
      class="absolute inset-0 h-full overflow-auto select-none"
      @click.self="() => onClose()"
    >
      <div class="flex flex-col max-w-xl mx-auto bg-gray-100">
        <div class="sticky top-0">
          <button
            type="button"
            @click="onClose()"
            class="text-green-800 absolute right-2 top-2"
            aria-label="close"
          >
            <Icon size="32" name="mdi:close" />
          </button>

          <p class="text-center font-bold bg-gray-100 p-2">絞り込み検索</p>
        </div>

        <div
          class="overflow-y-scroll"
          v-for="(cate_name, category) in cate"
          :key="`${cate_name}-${category}`"
        >
          <div
            v-if="realAttrs.filter((o) => o.category == category).length"
            class="p-2 mb-2"
          >
            <p class="font-bold text-lg mb-2">{{ cate_name }}</p>
            <ul class="flex flex-wrap gap-4">
              <li
                v-for="a in realAttrs.filter((o) => o.category == category)"
                :key="`${cate_name}-${a.cate_name}`"
              >
                <button
                  @click="onClick(a)"
                  :class="
                    selectAttr.includes(a.attr_num) ? 'selected' : 'normal'
                  "
                  class="px-2 py-1 whitespace-nowrap border rounded disabled:opacity-20"
                  :disabled="a.disabled"
                >
                  {{ a.cate_name }}
                </button>
              </li>
            </ul>
          </div>
        </div>
        <div class="w-full p-2 sticky bottom-0 bg-gray-200 flex items-center">
          <button class="btn hover:bg-gray-300" @click="reset">リセット</button>
          <span class="flex flex-1 text-right items-end justify-end">
            <span v-if="showCount" class="mr-2 text-2xl">
              {{ showCount }}
              <span class="text-xs">件</span>
            </span>

            <button class="btn text-white bg-blue-600" @click="search">
              検索
            </button>
          </span>
        </div>
      </div>
    </div>
  </VueFinalModal>
</template>

<style lang="scss" scoped>
.btn {
  @apply px-8 py-2 rounded;
}
.normal {
  @apply pc:hover:text-white pc:hover:bg-gray-500 bg-white;
}

.selected {
  @apply bg-gray-500 text-white border-gray-500;
}
</style>
